import { useState, useEffect, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";

interface CardDropdownProps {
  query: string;
  position?: { x: number; y: number };
  onSelect: (card: Scryfall.Card) => void;
  activeIndex: number;
  className?: string;
}

export function CardDropdown({
  query,
  position,
  onSelect,
  activeIndex,
  className,
}: CardDropdownProps) {
  const [results, setResults] = useState<Scryfall.Card[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const fetchResults = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(
        `/api/cards/search?q=${encodeURIComponent(q)}`
      );
      const data = await res.json();
      setResults(data.data || []);
    } catch (e) {
      console.error("Card search error:", e);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      fetchResults(query);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, fetchResults]);

  // Scroll active item into view
  useEffect(() => {
    if (listRef.current && activeIndex >= 0) {
      const items = listRef.current.querySelectorAll("[data-dropdown-item]");
      items[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const showNoResults = !isLoading && results.length === 0 && query.trim().length >= 2;

  return (
    <div
      style={position ? {
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 9999,
      } : undefined}
      className={cn(
        "w-64 max-h-48 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg",
        className
      )}
      ref={listRef}
    >
      {isLoading && (
        <div className="px-3 py-2 text-sm text-muted-foreground animate-pulse">
          Searching...
        </div>
      )}
      {showNoResults && (
        <div className="px-3 py-2 text-sm text-muted-foreground">
          No cards found
        </div>
      )}
      {results.map((card, i) => {
        const imageUrl =
          card.image_uris?.small ||
          card.card_faces?.[0]?.image_uris?.small;
        return (
          <button
            key={card.id}
            data-dropdown-item
            type="button"
            className={`flex items-center gap-2 w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${
              i === activeIndex ? "bg-accent text-accent-foreground" : ""
            }`}
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent textarea blur
              onSelect(card);
            }}
          >
            {imageUrl && (
              <img
                src={imageUrl}
                alt=""
                className="w-6 h-8 rounded-sm object-cover shrink-0"
              />
            )}
            <span className="truncate">{card.name}</span>
          </button>
        );
      })}
    </div>
  );
}
